import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    LogOut,
    RefreshCw,
    ShoppingBag,
    Phone,
    MapPin,
    Clock,
    CreditCard,
    Utensils,
    MessageSquare,
    Plus,
    Pencil,
    Trash2,
    Star,
    PartyPopper,
    Users,
    Megaphone,
    Calendar as CalIcon,
    Bell,
    Send,
    TrendingUp,
    Upload,
    Archive,
    ArchiveRestore,
    Settings as SettingsIcon,
    Globe,
    Building2,
    MailWarning,
} from "lucide-react";
import api from "../api";
import { useApp } from "../AppContext";
import { toast } from "sonner";
import DevBranding from "../components/DevBranding";
import OrderCalendar from "../components/OrderCalendar";
import TenantsPanel from "../components/TenantsPanel";
import SettingsPanel from "../components/admin/SettingsPanel";
import ProfitabilityPanel from "../components/admin/ProfitabilityPanel";
import UsersPanel from "../components/admin/UsersPanel";
import MarketingKit from "../components/admin/MarketingKit";
import { MenuEditorModal, TestimonialEditorModal } from "../components/admin/AdminModals";

const STATUS_OPTIONS = ["confirmed", "preparing", "ready", "delivered", "cancelled"];
const STATUS_COLORS = {
    pending_confirmation: "bg-terracotta-500 text-white",
    confirmed: "bg-leaf-400 text-white",
    new: "bg-terracotta-500 text-white",
    preparing: "bg-sand-400 text-earth-900",
    ready: "bg-leaf-500 text-white",
    delivered: "bg-earth-800 text-earth-50",
    cancelled: "bg-earth-300 text-earth-700",
};

const empty_menu_item = {
    slug: "",
    name_es: "",
    name_en: "",
    description_es: "",
    description_en: "",
    price: 0,
    image_url: "",
    category: "main",
    available: true,
    featured: false,
    serving_size_es: "",
    serving_size_en: "",
    default_quantity: 1,
    preorder_enabled: true,
    catering_only: false,
};

const empty_testimonial = {
    author: "",
    location: "",
    rating: 5,
    text_es: "",
    text_en: "",
    avatar_url: "",
    featured: true,
};

const AdminDashboard = () => {
    const { t, lang, changeLang } = useApp();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("orders");
    const [me, setMe] = useState(null); // { role, tenant_id, email, is_tenant_user }
    // Role gating: legacy admins (no is_tenant_user, no role) get full access
    // matching pre-multi-user behavior. Multi-user JWTs need role=owner for
    // sensitive tabs. Staff sees only orders/calendar/catering/menu/subs/testimonials.
    const isOwner = !me?.is_tenant_user || me?.role === "owner";
    // Super-admin (platform-owner) surfaces are gated to legacy admin sessions
    // — a tenant "owner" for one restaurant should NOT see the cross-tenant
    // Tenants panel. Phase 3 will issue a dedicated super-admin token.
    const isSuperAdmin = !me?.is_tenant_user;
    const [orderView, setOrderView] = useState("active"); // "active" | "archived"
    const [menu, setMenu] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [catering, setCatering] = useState([]);
    const [subscribers, setSubscribers] = useState({}); // {slug: [subs]}
    const [menuEditing, setMenuEditing] = useState(null);
    const [testiEditing, setTestiEditing] = useState(null);

    const fetchOrders = async (view = orderView) => {
        try {
            const r = await api.get("/admin/orders", {
                params: view === "archived" ? { archived: 1 } : {},
            });
            setOrders(r.data);
        } catch (e) {
            console.warn("fetchOrders failed:", e);
        } finally {
            setLoading(false);
        }
    };
    const fetchMenu = async () => {
        const r = await api.get("/menu");
        setMenu(r.data);
    };
    const fetchTestimonials = async () => {
        try {
            const r = await api.get("/admin/testimonials");
            setTestimonials(r.data);
        } catch (e) {
            console.warn("fetchTestimonials failed:", e);
        }
    };
    const fetchCatering = async () => {
        try {
            const r = await api.get("/admin/catering");
            setCatering(r.data);
        } catch (e) {
            console.warn("fetchCatering failed:", e);
        }
    };
    const fetchSubscribers = async () => {
        try {
            const r = await api.get("/admin/notify-subscribers");
            setSubscribers(r.data);
        } catch (e) {
            console.warn("fetchSubscribers failed:", e);
        }
    };
    const updateCateringStatus = async (id, status) => {
        try {
            await api.patch(`/admin/catering/${id}`, { status });
            setCatering((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
            toast.success(t.common.success);
        } catch {
            toast.error(t.common.error);
        }
    };
    const forceNotify = async (slug) => {
        if (!window.confirm(`¿Enviar emails a todos los suscriptores de ${slug}?`)) return;
        try {
            await api.post(`/admin/notify-send/${slug}`);
            toast.success("Notificaciones enviadas");
            fetchSubscribers();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        }
    };

    useEffect(() => {
        // Mount-only: gate everything on a fresh /me check, then prime the
        // tab fetchers ONCE. Subsequent updates come from the polling interval
        // below and from explicit refresh calls. Deliberately empty deps —
        // the fetchers are non-memoized so listing them here would trigger a
        // render → new-ref → re-fetch loop.
        let cancelled = false;
        api.get("/admin/me")
            .then((r) => {
                if (cancelled) return;
                setMe(r.data || null);
                fetchOrders(orderView);
                fetchMenu();
                fetchTestimonials();
                fetchCatering();
                fetchSubscribers();
            })
            .catch(() => {
                if (!cancelled) navigate("/admin/login");
            });
        const id = setInterval(() => {
            fetchOrders(orderView);
            fetchCatering();
            fetchSubscribers();
        }, 15000);
        return () => { cancelled = true; clearInterval(id); };
    }, [orderView]);

    const logout = async () => {
        try { await api.post("/admin/logout"); } catch (err) {
            console.warn("Logout request failed:", err);
        }
        try { localStorage.removeItem("admin_token"); } catch (err) {
            console.debug("localStorage cleanup failed:", err);
        }
        navigate("/admin/login");
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/orders/${id}`, { status });
            setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
            toast.success(t.common.success);
        } catch {
            toast.error(t.common.error);
        }
    };
    const confirmOrder = async (id) => {
        try {
            const r = await api.post(`/admin/orders/${id}/confirm`);
            setOrders((os) =>
                os.map((o) =>
                    o.id === id
                        ? { ...o, status: "confirmed", confirmed_at: r.data.order.confirmed_at, confirmed_by: r.data.order.confirmed_by }
                        : o
                )
            );
            toast.success(
                <div>
                    Pedido confirmado.{" "}
                    {r.data.customer_whatsapp_url && (
                        <a
                            href={r.data.customer_whatsapp_url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline font-bold"
                        >
                            Enviar WhatsApp al cliente →
                        </a>
                    )}
                </div>
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        }
    };
    const confirmCatering = async (id) => {
        try {
            await api.post(`/admin/catering/${id}/confirm`);
            setCatering((cs) =>
                cs.map((c) => (c.id === id ? { ...c, status: "confirmed" } : c))
            );
            toast.success("Catering confirmado");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        }
    };
    const markPaid = async (id) => {
        try {
            await api.patch(`/admin/orders/${id}`, { payment_status: "paid" });
            setOrders((os) =>
                os.map((o) => (o.id === id ? { ...o, payment_status: "paid" } : o))
            );
            toast.success(t.common.success);
        } catch {
            toast.error(t.common.error);
        }
    };

    const archiveOrder = async (id) => {
        if (!window.confirm("¿Archivar este pedido? Podrás consultarlo más tarde desde 'Archivados'.")) return;
        try {
            await api.post(`/admin/orders/${id}/archive`);
            setOrders((os) => os.filter((o) => o.id !== id));
            toast.success("Pedido archivado");
        } catch {
            toast.error(t.common.error);
        }
    };

    const unarchiveOrder = async (id) => {
        try {
            await api.post(`/admin/orders/${id}/unarchive`);
            setOrders((os) => os.filter((o) => o.id !== id));
            toast.success("Pedido restaurado");
        } catch {
            toast.error(t.common.error);
        }
    };

    const deleteOrder = async (id) => {
        if (!window.confirm("⚠️ ¿Eliminar este pedido permanentemente? Esta acción no se puede deshacer.")) return;
        try {
            await api.delete(`/admin/orders/${id}`);
            setOrders((os) => os.filter((o) => o.id !== id));
            toast.success("Pedido eliminado");
        } catch {
            toast.error(t.common.error);
        }
    };

    const uploadMenuPhoto = async (item, file) => {
        if (!file) return null;
        if (file.size > 6 * 1024 * 1024) {
            toast.error("Imagen demasiado grande (máx 6 MB)");
            return null;
        }
        const fd = new FormData();
        fd.append("file", file);
        if (item?.slug) fd.append("slug", item.slug);
        try {
            const r = await api.post("/admin/menu/upload-image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const newUrl = r.data.image_url;
            if (item?.id) {
                await api.put(`/admin/menu/${item.id}`, { ...item, image_url: newUrl });
                await fetchMenu();
                toast.success("Foto actualizada");
            }
            return newUrl;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al subir la foto");
            return null;
        }
    };

    const triggerCardUpload = (item) => {
        const inp = document.createElement("input");
        inp.type = "file";
        inp.accept = "image/*";
        inp.onchange = (e) => {
            const f = e.target.files?.[0];
            if (f) uploadMenuPhoto(item, f);
        };
        inp.click();
    };

    const saveMenuItem = async (item) => {
        try {
            if (item.id) {
                await api.put(`/admin/menu/${item.id}`, item);
            } else {
                await api.post("/admin/menu", item);
            }
            await fetchMenu();
            setMenuEditing(null);
            toast.success(t.common.success);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        }
    };
    const deleteMenuItem = async (id) => {
        if (!window.confirm("¿Eliminar este plato?")) return;
        try {
            await api.delete(`/admin/menu/${id}`);
            await fetchMenu();
            toast.success(t.common.success);
        } catch {
            toast.error(t.common.error);
        }
    };

    const saveTestimonial = async (item) => {
        try {
            await api.post("/admin/testimonials", item);
            await fetchTestimonials();
            setTestiEditing(null);
            toast.success(t.common.success);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t.common.error);
        }
    };
    const deleteTestimonial = async (id) => {
        if (!window.confirm("¿Eliminar este testimonio?")) return;
        try {
            await api.delete(`/admin/testimonials/${id}`);
            await fetchTestimonials();
            toast.success(t.common.success);
        } catch {
            toast.error(t.common.error);
        }
    };

    const stats = {
        total: orders.length,
        pending: orders.filter((o) => o.status === "pending_confirmation").length,
        revenue: orders
            .filter((o) => o.payment_status === "paid")
            .reduce((s, o) => s + o.total, 0),
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-earth-50 grain">
            {me?.impersonated_by && (
                <div data-testid="impersonation-banner" className="bg-red-600 text-white sticky top-0 z-40 shadow-lg">
                    <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs uppercase tracking-widest font-black bg-white/20 px-2 py-0.5 rounded">
                                Impersonating
                            </span>
                            <span className="font-semibold truncate">
                                {me.email} <span className="opacity-70">· {me.role}</span>
                            </span>
                        </div>
                        <button
                            type="button"
                            data-testid="impersonation-exit-btn"
                            onClick={async () => {
                                try { await api.post("/admin/logout"); } catch { /* ignore */ }
                                navigate("/admin/login");
                            }}
                            className="px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                        >
                            Exit → Log back in
                        </button>
                    </div>
                </div>
            )}
            <header className="bg-earth-900 text-earth-50 sticky top-0 z-30 shadow-lg">
                <div className="absolute top-0 left-0 right-0 h-1 flex">
                    <div className="flex-1" style={{ background: "#002D62" }} />
                    <div className="flex-1 bg-white" />
                    <div className="flex-1" style={{ background: "#CE1126" }} />
                </div>
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-terracotta-500 flex items-center justify-center text-white font-serif font-bold">
                            G&amp;M
                        </div>
                        <div className="leading-tight">
                            <div className="font-serif text-lg font-bold">Admin Panel</div>
                            <div className="text-xs text-earth-300">Delicias Dominicanas</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="admin-lang-toggle"
                            onClick={() => changeLang(lang === "es" ? "en" : "es")}
                            title="Cambiar idioma / Change language"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-earth-800 hover:bg-earth-700 text-earth-100 text-xs font-bold uppercase tracking-wider"
                        >
                            <Globe className="w-3.5 h-3.5" />
                            {lang === "es" ? "EN" : "ES"}
                        </button>
                        <button
                            data-testid="admin-refresh-btn"
                            onClick={() => {
                                fetchOrders();
                                fetchMenu();
                                fetchTestimonials();
                            }}
                            className="p-2 rounded-full hover:bg-earth-800"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                            data-testid="admin-billing-portal-btn"
                            onClick={async () => {
                                try {
                                    const tenantSlug = new URLSearchParams(window.location.search).get("slug") || "delicias";
                                    const r = await api.post("/billing/portal", {
                                        slug: tenantSlug,
                                        return_url: window.location.href,
                                    });
                                    if (r.data?.url) window.location.assign(r.data.url);
                                } catch (e) {
                                    const msg = e?.response?.data?.detail || "Billing portal unavailable";
                                    alert(msg);
                                }
                            }}
                            title="Manage billing (Stripe portal)"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-earth-800 hover:bg-earth-700 text-earth-50 text-sm font-semibold"
                        >
                            <CreditCard className="w-4 h-4" />
                            <span className="hidden sm:inline">{lang === "es" ? "Facturación" : "Billing"}</span>
                        </button>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white text-sm font-semibold"
                        >
                            <LogOut className="w-4 h-4" />
                            {t.admin.logout}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
                    <StatCard label="Total Orders" value={stats.total} icon={ShoppingBag} />
                    <StatCard label={t.admin.pending || t.admin.new} value={stats.pending} icon={Clock} accent />
                    <StatCard label="Revenue (Paid)" value={`$${stats.revenue.toFixed(2)}`} icon={CreditCard} />
                </div>

                <div className="flex gap-2 bg-earth-100 rounded-full p-1 max-w-3xl mb-8 flex-wrap">
                    <TabBtn id="orders" tab={tab} setTab={setTab} label={t.admin.tabs.orders} icon={ShoppingBag} />
                    <TabBtn id="calendar" tab={tab} setTab={setTab} label={t.admin.tabs.calendar} icon={CalIcon} />
                    <TabBtn id="catering" tab={tab} setTab={setTab} label={t.admin.tabs.catering} icon={PartyPopper} />
                    <TabBtn id="menu" tab={tab} setTab={setTab} label={t.admin.tabs.menu} icon={Utensils} />
                    <TabBtn id="subscribers" tab={tab} setTab={setTab} label={t.admin.tabs.subscribers} icon={Bell} />
                    <TabBtn id="testimonials" tab={tab} setTab={setTab} label={t.admin.tabs.testimonials} icon={MessageSquare} />
                    {isOwner && <TabBtn id="profitability" tab={tab} setTab={setTab} label={t.admin.tabs.profitability} icon={TrendingUp} />}
                    {isOwner && <TabBtn id="users" tab={tab} setTab={setTab} label={lang === "es" ? "Equipo" : "Team"} icon={Users} />}
                    {isSuperAdmin && <TabBtn id="tenants" tab={tab} setTab={setTab} label={t.admin.tabs.tenants} icon={Building2} />}
                    {isSuperAdmin && <TabBtn id="marketing" tab={tab} setTab={setTab} label={lang === "es" ? "Marketing" : "Marketing"} icon={Megaphone} />}
                    {isOwner && <TabBtn id="settings" tab={tab} setTab={setTab} label={t.admin.tabs.settings} icon={SettingsIcon} />}
                </div>

                {tab === "orders" && (
                    <OrdersTab
                        orders={orders}
                        loading={loading}
                        orderView={orderView}
                        setOrderView={setOrderView}
                        t={t}
                        onStatusChange={updateStatus}
                        onMarkPaid={markPaid}
                        onConfirm={confirmOrder}
                        onArchive={archiveOrder}
                        onUnarchive={unarchiveOrder}
                        onDelete={deleteOrder}
                    />
                )}

                {tab === "menu" && (
                    <MenuTab
                        menu={menu}
                        onAdd={() => setMenuEditing({ ...empty_menu_item })}
                        onEdit={(m) => setMenuEditing(m)}
                        onDelete={deleteMenuItem}
                        onUpload={triggerCardUpload}
                    />
                )}

                {tab === "catering" && (
                    <CateringTab
                        catering={catering}
                        lang={lang}
                        onStatus={updateCateringStatus}
                        onConfirm={confirmCatering}
                    />
                )}

                {tab === "subscribers" && (
                    <SubscribersTab
                        subscribers={subscribers}
                        menu={menu}
                        onForceNotify={forceNotify}
                    />
                )}

                {tab === "testimonials" && (
                    <TestimonialsTab
                        testimonials={testimonials}
                        onAdd={() => setTestiEditing({ ...empty_testimonial })}
                        onDelete={deleteTestimonial}
                    />
                )}

                {tab === "profitability" && isOwner && <ProfitabilityPanel />}

                {tab === "calendar" && <OrderCalendar />}

                {tab === "tenants" && isSuperAdmin && <TenantsPanel lang={lang} />}

                {tab === "marketing" && isSuperAdmin && <MarketingKit />}

                {tab === "settings" && isOwner && <SettingsPanel />}

                {tab === "users" && isOwner && (
                    <div className="space-y-4">
                        <div className="text-earth-800">
                            <h2 className="font-serif font-bold text-2xl">
                                {lang === "es" ? "Equipo" : "Team"}
                            </h2>
                            <p className="text-sm text-earth-600">
                                {lang === "es"
                                    ? "Invita a otros usuarios para ayudar a manejar tu restaurante. Ellos podrán ver órdenes y actualizar el menú."
                                    : "Invite other users to help manage your restaurant. They can view orders and update the menu."}
                            </p>
                        </div>
                        <UsersPanel tenantSlug={me?.tenant_id || "delicias-original"} mode="owner" />
                    </div>
                )}
            </main>

            {menuEditing && (
                <MenuEditorModal
                    item={menuEditing}
                    onClose={() => setMenuEditing(null)}
                    onSave={saveMenuItem}
                />
            )}
            {testiEditing && (
                <TestimonialEditorModal
                    item={testiEditing}
                    onClose={() => setTestiEditing(null)}
                    onSave={saveTestimonial}
                />
            )}
            <DevBranding variant="page" />
        </div>
    );
};

// ============= Tab subcomponents (data + handlers passed via props) =============

const OrdersTab = ({ orders, loading, orderView, setOrderView, t, onStatusChange, onMarkPaid, onConfirm, onArchive, onUnarchive, onDelete }) => (
    <section data-testid="admin-orders-list">
        <div className="flex items-center gap-2 bg-earth-100 rounded-full p-1 mb-6 w-fit">
            <button
                data-testid="orders-view-active-btn"
                onClick={() => setOrderView("active")}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    orderView === "active" ? "bg-white text-earth-900 shadow-sm" : "text-earth-600 hover:text-earth-900"
                }`}
            >
                <ShoppingBag className="w-4 h-4" /> {t.admin.active}
            </button>
            <button
                data-testid="orders-view-archived-btn"
                onClick={() => setOrderView("archived")}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    orderView === "archived" ? "bg-white text-earth-900 shadow-sm" : "text-earth-600 hover:text-earth-900"
                }`}
            >
                <Archive className="w-4 h-4" /> {t.admin.archived}
            </button>
        </div>
        {loading ? (
            <div className="text-earth-600">{t.common.loading}</div>
        ) : orders.length === 0 ? (
            <div className="bg-white rounded-3xl p-14 text-center text-earth-600 border border-earth-200">
                {orderView === "archived" ? (
                    <Archive className="w-12 h-12 mx-auto mb-4 text-earth-300" />
                ) : (
                    <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-earth-300" />
                )}
                {orderView === "archived" ? "No hay pedidos archivados." : t.admin.noOrders}
            </div>
        ) : (
            <div className="space-y-5">
                {orders.map((o) => (
                    <OrderCard
                        key={o.id}
                        order={o}
                        onStatusChange={onStatusChange}
                        onMarkPaid={onMarkPaid}
                        onConfirm={onConfirm}
                        onArchive={onArchive}
                        onUnarchive={onUnarchive}
                        onDelete={onDelete}
                        archivedView={orderView === "archived"}
                        t={t}
                    />
                ))}
            </div>
        )}
    </section>
);

const MenuTab = ({ menu, onAdd, onEdit, onDelete, onUpload }) => (
    <section data-testid="admin-menu-list">
        <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl font-bold text-earth-900">Platos ({menu.length})</h2>
            <button
                data-testid="add-menu-item-btn"
                onClick={onAdd}
                className="inline-flex items-center gap-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white px-5 py-2.5 text-sm font-semibold"
            >
                <Plus className="w-4 h-4" /> Agregar plato
            </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {menu.map((m) => (
                <div
                    key={m.id}
                    data-testid={`admin-menu-${m.slug}`}
                    className="bg-white border border-earth-200 rounded-3xl overflow-hidden group"
                >
                    <div className="relative">
                        <img src={m.image_url} alt={m.name_es} className="w-full h-44 object-cover" />
                        <div className="absolute inset-0 bg-earth-900/0 group-hover:bg-earth-900/60 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                            <button onClick={() => onUpload(m)} data-testid={`upload-menu-${m.slug}-btn`} title="Cambiar foto" className="p-2.5 rounded-full bg-white text-earth-900 hover:bg-leaf-500 hover:text-white">
                                <Upload className="w-4 h-4" />
                            </button>
                            <button onClick={() => onEdit(m)} data-testid={`edit-menu-${m.slug}-btn`} title="Editar" className="p-2.5 rounded-full bg-white text-earth-900 hover:bg-terracotta-500 hover:text-white">
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => onDelete(m.id)} data-testid={`delete-menu-${m.slug}-btn`} title="Eliminar" className="p-2.5 rounded-full bg-white text-earth-900 hover:bg-red-500 hover:text-white">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        {m.featured && (
                            <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-terracotta-500 text-white text-[10px] font-bold uppercase">
                                <Star className="w-3 h-3 fill-current" /> Favorito
                            </span>
                        )}
                    </div>
                    <div className="p-5">
                        <div className="font-serif font-bold text-earth-900 text-lg">{m.name_es}</div>
                        <div className="text-xs text-earth-500 mt-0.5">{m.name_en}</div>
                        <div className="text-terracotta-600 font-bold mt-2">${m.price.toFixed(2)}</div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);

const CateringTab = ({ catering, lang, onStatus }) => (
    <section data-testid="admin-catering-list">
        <h2 className="font-serif text-2xl font-bold text-earth-900 mb-5">
            {lang === "es" ? "Solicitudes de catering" : "Catering requests"} ({catering.length})
        </h2>
        {catering.length === 0 ? (
            <div className="bg-white rounded-3xl p-14 text-center text-earth-600 border border-earth-200">
                <PartyPopper className="w-12 h-12 mx-auto mb-4 text-earth-300" />
                Aún no hay solicitudes.
            </div>
        ) : (
            <div className="space-y-4">
                {catering.map((c) => (
                    <article
                        key={c.id}
                        data-testid={`catering-${c.id}`}
                        className="bg-white rounded-3xl border border-earth-200 p-6"
                    >
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                            <div>
                                <div className="font-serif font-bold text-xl text-earth-900">{c.customer_name}</div>
                                <a
                                    href={`https://wa.me/${c.customer_phone.replace(/[^\d]/g, "")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm text-terracotta-600 inline-flex items-center gap-1 mt-0.5"
                                >
                                    <Phone className="w-3.5 h-3.5" />
                                    {c.customer_phone}
                                </a>
                            </div>
                            <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider ${
                                    c.status === "confirmed" ? "bg-leaf-500 text-white" :
                                    c.status === "quoted" ? "bg-sand-400 text-earth-900" :
                                    c.status === "cancelled" ? "bg-earth-300 text-earth-700" :
                                    "bg-terracotta-500 text-white"
                                }`}
                            >
                                {c.status}
                            </span>
                        </div>
                        <div className="grid sm:grid-cols-4 gap-4 text-sm">
                            <CateringStat icon={CalIcon} label="Fecha" value={c.event_date} />
                            <CateringStat icon={PartyPopper} label="Tipo" value={c.event_type} />
                            <CateringStat icon={Users} label="Invitados" value={c.guest_count} />
                            <CateringStat icon={CreditCard} label="Presupuesto" value={c.budget ? `$${c.budget.toFixed(2)}` : "—"} />
                        </div>
                        {c.notes && (
                            <div className="mt-4 text-sm text-earth-700 italic">&ldquo;{c.notes}&rdquo;</div>
                        )}
                        <div className="mt-5 pt-5 border-t border-earth-100 flex flex-wrap gap-2">
                            {["new", "quoted", "confirmed", "cancelled"].map((s) => (
                                <button
                                    key={s}
                                    data-testid={`catering-${c.id}-status-${s}-btn`}
                                    onClick={() => onStatus(c.id, s)}
                                    className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                        c.status === s ? "bg-earth-900 text-earth-50" : "bg-earth-100 hover:bg-earth-200 text-earth-700"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </article>
                ))}
            </div>
        )}
    </section>
);

const SubscribersTab = ({ subscribers, menu, onForceNotify }) => (
    <section data-testid="admin-subscribers-list">
        <h2 className="font-serif text-2xl font-bold text-earth-900 mb-2">
            Lista de espera ({Object.values(subscribers).reduce((s, arr) => s + arr.length, 0)})
        </h2>
        <p className="text-sm text-earth-600 mb-6">
            Personas inscritas para recibir aviso cuando un plato esté disponible. Al marcar el plato como &ldquo;Disponible&rdquo; en el menú, se envía un email automático.
        </p>
        {Object.keys(subscribers).length === 0 ? (
            <div className="bg-white rounded-3xl p-14 text-center text-earth-600 border border-earth-200">
                <Bell className="w-12 h-12 mx-auto mb-4 text-earth-300" />
                Aún no hay suscriptores.
            </div>
        ) : (
            <div className="space-y-5">
                {Object.entries(subscribers).map(([slug, subs]) => {
                    const item = menu.find((m) => m.slug === slug);
                    return (
                        <article
                            key={slug}
                            data-testid={`subscribers-${slug}`}
                            className="bg-white rounded-3xl border border-earth-200 overflow-hidden"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-earth-100">
                                <div className="flex items-center gap-4">
                                    {item && (
                                        <img src={item.image_url} alt={item.name_es} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                                    )}
                                    <div>
                                        <div className="font-serif font-bold text-lg text-earth-900">
                                            {item ? item.name_es : slug}
                                        </div>
                                        <div className="text-xs text-earth-500">
                                            {subs.length} suscriptor{subs.length !== 1 ? "es" : ""}
                                            {item && (
                                                <> · <span className={item.available ? "text-leaf-600 font-bold" : "text-terracotta-600 font-bold"}>
                                                    {item.available ? "Disponible" : "Coming Soon"}
                                                </span></>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    data-testid={`force-notify-${slug}-btn`}
                                    onClick={() => onForceNotify(slug)}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white text-xs font-bold uppercase tracking-wider"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                    Reenviar avisos
                                </button>
                            </div>
                            <ul className="divide-y divide-earth-100">
                                {subs.map((s) => (
                                    <li key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                                        <Phone className="w-3.5 h-3.5 text-earth-500 shrink-0" />
                                        <a
                                            href={`https://wa.me/${s.phone}?text=${encodeURIComponent(
                                                `Hola! Te avisamos que ya tenemos ${item ? item.name_es : slug} disponible. ¡Haz tu pedido!`
                                            )}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-sm font-semibold text-terracotta-600 hover:underline"
                                        >
                                            +{s.phone}
                                        </a>
                                        {s.email && (
                                            <span className="text-xs text-earth-600">· {s.email}</span>
                                        )}
                                        <span className="text-xs text-earth-400 ml-auto">
                                            {new Date(s.created_at).toLocaleDateString()}
                                        </span>
                                        {s.notified && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-leaf-500 text-white text-[10px] font-bold uppercase tracking-wider">
                                                Enviado
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    );
                })}
            </div>
        )}
    </section>
);

const TestimonialsTab = ({ testimonials, onAdd, onDelete }) => (
    <section data-testid="admin-testimonials-list">
        <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl font-bold text-earth-900">
                Testimonios ({testimonials.length})
            </h2>
            <button
                data-testid="add-testimonial-btn"
                onClick={onAdd}
                className="inline-flex items-center gap-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white px-5 py-2.5 text-sm font-semibold"
            >
                <Plus className="w-4 h-4" /> Agregar testimonio
            </button>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
            {testimonials.map((tm) => (
                <div key={tm.id} className="bg-white border border-earth-200 rounded-3xl p-6">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="font-bold text-earth-900">{tm.author}</div>
                            <div className="text-xs text-earth-500">{tm.location}</div>
                        </div>
                        <button onClick={() => onDelete(tm.id)} className="p-2 rounded-full hover:bg-red-50 text-red-500">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex gap-0.5 my-2">
                        {Array.from({ length: tm.rating || 5 }).map((_, k) => (
                            <Star key={`star-${tm.id}-${k}`} className="w-3 h-3 fill-terracotta-500 text-terracotta-500" />
                        ))}
                    </div>
                    <p className="text-sm text-earth-700 italic font-serif">&ldquo;{tm.text_es}&rdquo;</p>
                </div>
            ))}
        </div>
    </section>
);

// ============= Small UI primitives kept inline (used only here) =============

const CateringStat = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-full bg-earth-100 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-earth-600" />
        </div>
        <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-wider text-earth-500 font-bold">{label}</div>
            <div className="font-semibold text-earth-900">{value}</div>
        </div>
    </div>
);

const TabBtn = ({ id, tab, setTab, label, icon: Icon }) => (
    <button
        data-testid={`admin-tab-${id}-btn`}
        onClick={() => setTab(id)}
        className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
            tab === id ? "bg-white text-earth-900 shadow-sm" : "text-earth-600"
        }`}
    >
        <Icon className="w-3.5 h-3.5" />
        {label}
    </button>
);

const StatCard = ({ label, value, icon: Icon, accent }) => (
    <div className={`rounded-3xl p-6 border ${accent ? "bg-terracotta-500 text-white border-terracotta-500" : "bg-white text-earth-900 border-earth-200"}`}>
        <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider font-bold opacity-80">{label}</div>
            <Icon className="w-5 h-5 opacity-80" />
        </div>
        <div className="font-serif font-black text-4xl">{value}</div>
    </div>
);

const OrderCard = ({ order, onStatusChange, onMarkPaid, onConfirm, onArchive, onUnarchive, onDelete, archivedView, t }) => (
    <article
        data-testid={`admin-order-${order.id}`}
        className={`bg-white rounded-3xl border-2 p-6 shadow-sm transition-colors ${
            archivedView ? "border-earth-300 opacity-90" :
            order.status === "pending_confirmation" ? "border-terracotta-400 ring-2 ring-terracotta-200/40" :
            "border-earth-200"
        }`}
    >
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
                <div className="font-serif font-bold text-xl text-earth-900 flex items-center gap-2">
                    #{order.id.slice(0, 8).toUpperCase()}
                    {archivedView && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-earth-200 text-earth-700 text-[10px] font-bold uppercase tracking-wider">
                            <Archive className="w-3 h-3" /> Archivado
                        </span>
                    )}
                    {order.email_status && order.email_status !== "sent" && (
                        <span
                            data-testid={`order-email-warn-${order.id}`}
                            title={
                                order.email_status === "skipped_unverified"
                                    ? "Notificación por email no enviada: dominio Resend no verificado"
                                    : order.email_status === "skipped_unconfigured"
                                    ? "Notificación por email no enviada: Resend no configurado"
                                    : `Notificación por email falló: ${order.email_error || "error desconocido"}`
                            }
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-700 text-[10px] font-bold uppercase tracking-wider cursor-help"
                        >
                            <MailWarning className="w-3 h-3" /> Email
                        </span>
                    )}
                </div>
                <div className="text-xs text-earth-500 mt-0.5">{new Date(order.created_at).toLocaleString()}</div>
                {order.requested_date && (
                    <div className="text-xs text-earth-700 mt-1">
                        📆 Fecha solicitada: <b>{order.requested_date}</b>
                    </div>
                )}
                {order.has_preorder && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-bold uppercase tracking-wider">
                        ⏳ Pre-orden 72h
                    </span>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider ${STATUS_COLORS[order.status] || "bg-earth-200"}`}>
                    {t.admin[order.status] || order.status}
                </span>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider ${
                    order.payment_status === "paid" ? "bg-leaf-500 text-white" :
                    order.payment_status === "cash_on_pickup" ? "bg-sand-400 text-earth-900" :
                    "bg-earth-200 text-earth-800"
                }`}>
                    {order.payment_status === "paid" && t.admin.paid}
                    {order.payment_status === "cash_on_pickup" && t.admin.cashOnPickup}
                    {order.payment_status === "pending" && t.admin.pending}
                </span>
            </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-5">
            <div>
                <div className="text-xs uppercase tracking-wider font-bold text-earth-500 mb-2">Cliente</div>
                <div className="font-semibold text-earth-900">{order.customer_name}</div>
                <a href={`https://wa.me/${order.customer_phone.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-sm text-terracotta-600 hover:underline inline-flex items-center gap-1 mt-1">
                    <Phone className="w-3.5 h-3.5" />{order.customer_phone}
                </a>
                {order.delivery_address && (
                    <div className="text-sm text-earth-700 mt-1 inline-flex items-start gap-1">
                        <MapPin className="w-3.5 h-3.5 mt-0.5" />{order.delivery_address}
                    </div>
                )}
                {order.notes && <div className="text-xs text-earth-600 mt-2 italic">&ldquo;{order.notes}&rdquo;</div>}
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider font-bold text-earth-500 mb-2">Items</div>
                <ul className="text-sm space-y-1">
                    {order.items.map((it, i) => (
                        <li key={`${order.id}-item-${i}`} className="flex justify-between text-earth-800">
                            <span>
                                {it.quantity}× {it.name}
                                {it.pre_order && <span className="ml-1 text-[10px] text-blue-700 font-bold">⏳</span>}
                            </span>
                            <span className="font-semibold">${(it.price * it.quantity).toFixed(2)}</span>
                        </li>
                    ))}
                </ul>
                <div className="flex justify-between mt-3 pt-3 border-t border-earth-100">
                    <span className="text-sm font-bold uppercase tracking-wider text-earth-600">Total</span>
                    <span className="font-serif font-black text-2xl text-terracotta-600">${order.total.toFixed(2)}</span>
                </div>
            </div>
        </div>
        {order.status === "pending_confirmation" && (
            <div className="mt-5 pt-5 border-t border-terracotta-200 flex flex-wrap items-center gap-3">
                <div className="text-xs uppercase tracking-wider font-bold text-terracotta-600">
                    ⚠ Pedido pendiente de confirmación
                </div>
                <button
                    onClick={() => onConfirm(order.id)}
                    data-testid={`confirm-order-${order.id}-btn`}
                    className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white text-xs font-bold uppercase tracking-wider"
                >
                    ✓ Confirmar pedido
                </button>
            </div>
        )}
        <div className="mt-5 pt-5 border-t border-earth-100 flex flex-wrap items-center gap-2">
            <div className="text-xs uppercase tracking-wider font-bold text-earth-500 mr-2">Estado:</div>
            {STATUS_OPTIONS.map((s) => (
                <button
                    key={s}
                    data-testid={`status-${s}-${order.id}-btn`}
                    onClick={() => onStatusChange(order.id, s)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                        order.status === s ? STATUS_COLORS[s] : "bg-earth-100 hover:bg-earth-200 text-earth-700"
                    }`}
                >
                    {t.admin[s] || s}
                </button>
            ))}
            {order.payment_status !== "paid" && (
                <button
                    onClick={() => onMarkPaid(order.id)}
                    data-testid={`mark-paid-${order.id}-btn`}
                    className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-leaf-500 hover:bg-leaf-600 text-white text-[10px] font-bold uppercase tracking-wider"
                >
                    <CreditCard className="w-3 h-3" /> Marcar pagado
                </button>
            )}
        </div>
        <div className="mt-3 pt-3 border-t border-earth-100 flex flex-wrap items-center justify-end gap-2">
            {archivedView ? (
                <button
                    onClick={() => onUnarchive(order.id)}
                    data-testid={`unarchive-order-${order.id}-btn`}
                    title="Restaurar a pedidos activos"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-earth-100 hover:bg-earth-200 text-earth-800 text-[11px] font-bold uppercase tracking-wider transition-colors"
                >
                    <ArchiveRestore className="w-3.5 h-3.5" /> Restaurar
                </button>
            ) : (
                <button
                    onClick={() => onArchive(order.id)}
                    data-testid={`archive-order-${order.id}-btn`}
                    title="Archivar — el pedido se ocultará del listado activo"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-earth-100 hover:bg-earth-200 text-earth-800 text-[11px] font-bold uppercase tracking-wider transition-colors"
                >
                    <Archive className="w-3.5 h-3.5" /> Archivar
                </button>
            )}
            <button
                onClick={() => onDelete(order.id)}
                data-testid={`delete-order-${order.id}-btn`}
                title="Eliminar permanentemente"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-500 hover:text-white text-red-600 text-[11px] font-bold uppercase tracking-wider transition-colors border border-red-200"
            >
                <Trash2 className="w-3.5 h-3.5" /> Eliminar
            </button>
        </div>
    </article>
);

export default AdminDashboard;
